import { Component, ViewChild, ElementRef, Output, EventEmitter, NgZone, signal, effect, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { Employee } from '@app/common-codes/models/matwins-tenant/Employee.model';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { PermissionsService } from '@app/common-codes/services/Permissions.service';
import { Employee_POS } from '@app/common-codes/models/matwins-tenant/navigation-entities/Employee_POS.model';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';


@Register(Employee)
@Component({
  selector: 'app-employee-detail',
  templateUrl: './employee-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent, MultiSelectComponent, FieldWrapperComponent, FieldContextDirective]
})
export class EmployeeDetailComponent extends GenetricDetailComponent<Employee> {


  override ComponentEntityClass = Employee;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  @Output() itemSaved = new EventEmitter<Employee>();
  // @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;



  multiSelectOptions: LookupItem[] = [];

  selected: string[] = [];


  employeePOSLookup = signal<LookupItem[]>([]);
  employeeStatusLookup = signal<LookupItem[]>([]);
  languageLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);
  managerLookup = signal<{ id: number; value: string }[]>([]);

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.employeeStatusLookup.set(this.getLookupData('EmployeeStatus'));
        this.languageLookup.set(this.getLookupData('i18nLanguage'));
        this.employeePOSLookup.set(this.multiSelectOptions);
        let posLookup = this.lookupService.getLookupData('PointOfService');
        this.pointOfServiceLookup.set(posLookup);

        // Managers disponibles pour fK_Manager (isManager + exclusion de soi-même)
        this.managerLookup.set(
          this.lookupService.getLookupData('Employee')
            .filter(e => e.additionalData?.['ismanager'] === true && Number(e.id) !== entity.id)
            .map(e => ({ id: Number(e.id), value: e.value }))
        );


        this.loadPos();

      }
    });
  }

  loadPos() {
    const entity = this.editingEntity();
    if (!entity) {
      return;
    }
    const selected = this.editingEntity()?.employeePOS?.map(ap => `${ap.fK_PointOfService}`) ?? [];
    if (!selected) {
      return;
    }
    entity.selectedPK_Pos = selected;
  }

  onPOSselectChange(evt: string[]) {
    const employee = this.editingEntity();
    if (!employee) {
      return;
    }

    // const newEntity = Object.assign(new Employee(), employee);


    const selectedPosIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    const existing = employee.employeePOS ?? [];
    employee.employeePOS = selectedPosIds.map(posId => {
      const found = existing.find(a => a.fK_PointOfService === posId);
      if (found) {
        return found;
      }
      const employeePos = new Employee_POS();
      employeePos.fK_PointOfService = posId;
      employeePos.fK_Employee = employee?.id ?? employeePos.fK_Employee;
      return employeePos;
    });

    employee.selectedPK_Pos = evt;

  }


  onMultiselectChange(evt: any) {
    console.log(">>>>> Selected ", evt);
  }

  updateEntityProperty(newValue: any, propertyName: string): void {
    this.editingEntity.update(entity => {
      if (entity && propertyName in entity) {
        entity[propertyName] = newValue;
      }
      return entity;
    });
  }

  override getEmptyEntity(): Employee {
    const employee = new Employee();
    employee.status = 1;
    const posId = this.matwinsVariables?.posId;
    if (posId) {
      const employeePos = new Employee_POS();
      employeePos.fK_PointOfService = posId;
      employeePos.fK_Employee = 0;
      employee.employeePOS = [employeePos];
      employee.selectedPK_Pos = employee.employeePOS.map(ep => `${ep.fK_PointOfService}`);
    }
    return employee;
  }

  override parseToEntityInstance(): void {
    if (this.EntityInstance && this.editingEntity()) {
      Object.assign(this.EntityInstance, this.editingEntity());
    }
  }

  performSavedItem(savedItem: Employee) {
    savedItem.fullName = `${savedItem.firstName} - ${savedItem.lastName}`;
  }

  async saveItem(): Promise<void> {
    const savedItem = await this.save();
    if (savedItem) {
      this.performSavedItem(savedItem);
      this.itemSaved.emit(savedItem);
    }
  }

  async saveAndCloseItem(): Promise<void> {
    const savedItem = await this.save();
    if (savedItem) {
      this.performSavedItem(savedItem);
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }



  downloadQrCode(): void {
    console.log('Download QR Code clicked');
    // Implement QR code download logic here
  }

  createAccount(): void {
    console.log('Create Account clicked');
    // Implement create account logic here
  }


  override updateFromCache(entity: Employee): void {
    super.updateFromCache(entity);

    const current = this.editingEntity();
    if (!current) return;

    // Recalculer selectedPK_Pos à partir de employeePOS
    if (entity.employeePOS) {
      const newSelected = entity.employeePOS
        .map(ep => ep.fK_PointOfService?.toString())
        .filter(k => k != null) as string[];

      if (JSON.stringify(current.selectedPK_Pos) !== JSON.stringify(newSelected)) {
        current.selectedPK_Pos = newSelected;
      }
    }
  }
}
